import React from 'react';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';
import { GroupedBudgetCategory } from '../../types/finance';

interface BudgetCategoryRowProps {
  category: GroupedBudgetCategory;
  isIncome?: boolean;
}

const FREQUENCY_SUFFIXES: Record<number, string> = {
  2: '/2mo',
  3: '/qtr',
  6: '/6mo',
  12: '/yr',
};

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const BudgetCategoryRow: React.FC<BudgetCategoryRowProps> = ({ category, isIncome }) => {
  const pct = category.effective_budget > 0
    ? Math.min((category.spent / category.effective_budget) * 100, 100)
    : 0;

  const barColor = category.status === 'over_budget'
    ? COLORS.status.error
    : category.status === 'warning'
      ? COLORS.status.warning
      : COLORS.status.success;

  const remainColor = category.remaining < 0
    ? COLORS.status.error
    : COLORS.text.secondary;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 100px 100px 100px',
      alignItems: 'center',
      padding: `${SPACING[8]} ${SPACING[12]}`,
      borderBottom: `1px solid ${COLORS.border.default}`,
      fontSize: TYPOGRAPHY.body.sizes.default,
      fontFamily: TYPOGRAPHY.body.fontFamily,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
        <span style={{ color: COLORS.text.primary }}>{category.category_name}</span>
        {category.rollover > 0 && (
          <span style={{
            fontSize: TYPOGRAPHY.body.sizes.tiny,
            color: COLORS.accent.default,
          }}>
            +{formatCurrency(category.rollover)} rollover
          </span>
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ color: COLORS.text.primary, fontWeight: '500' }}>
          {formatCurrency(category.budgeted)}
        </div>
        {category.frequency > 1 && category.full_amount != null && (
          <div style={{
            fontSize: TYPOGRAPHY.body.sizes.tiny,
            color: COLORS.text.tertiary,
          }}>
            {formatCurrency(category.full_amount)}{FREQUENCY_SUFFIXES[category.frequency] || `/${category.frequency}mo`}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', color: COLORS.text.primary }}>
        {formatCurrency(category.spent)}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ color: remainColor, fontWeight: '500', marginBottom: '2px' }}>
          {formatCurrency(category.remaining)}
        </div>
        {!isIncome && (
          <div style={{
            height: '3px',
            backgroundColor: COLORS.bg.hover,
            borderRadius: '2px',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${pct}%`,
              backgroundColor: barColor,
              borderRadius: '2px',
              transition: 'width 0.3s ease',
            }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetCategoryRow;
